import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const WeeklyCalendar = ({ events = [], daysCount = 7 }) => {
    const navigate = useNavigate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = [];
    for (let i = 0; i < daysCount; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        days.push(d);
    }

    const now = new Date();
    const nearestEventRef = useRef(null);

    const daysData = days.map((date, idx) => {
        const isToday = idx === 0;
        const dayEvents = events.filter(e => {
            const dStr = e.StartDateTime || e.StartDate || e.date;
            if (!dStr) return false;
            const eventDate = new Date(dStr);
            return eventDate.getFullYear() === date.getFullYear() &&
                eventDate.getMonth() === date.getMonth() &&
                eventDate.getDate() === date.getDate();
        }).sort((a, b) => {
            const timeA = new Date(a.StartDateTime || a.StartDate || a.date).getTime();
            const timeB = new Date(b.StartDateTime || b.StartDate || b.date).getTime();
            return timeA - timeB;
        });
        return { date, isToday, dayEvents, idx };
    });

    let globalNearestIdentifier = null;
    let minGlobalDiff = Infinity;

    daysData.forEach(day => {
        day.dayEvents.forEach((evt, eIdx) => {
            const timeStr = evt.StartDateTime || evt.StartDate || evt.date;
            if (timeStr) {
                const d = new Date(timeStr);
                const diff = d - now;
                if (diff >= 0 && diff < minGlobalDiff) {
                    minGlobalDiff = diff;
                    globalNearestIdentifier = `${day.idx}-${eIdx}`;
                }
            }
        });
    });

    useEffect(() => {
        if (nearestEventRef.current) {
            const parent = nearestEventRef.current.parentElement;
            if (parent) {
                // Scroll parent down to the nearest element
                parent.scrollTop = nearestEventRef.current.offsetTop - parent.offsetTop - 10;
            }
        }
    }, [events, daysCount]);

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const getEventColor = (category, subType) => {
        const cat = (category || '').toLowerCase();
        const sub = (subType || '').toLowerCase();

        if (cat === 'academic events') {
            if (sub.includes('conference')) return { bg: '#dbeafe', text: '#2563eb', border: '#bfdbfe' }; // Blue
            if (sub.includes('symposium')) return { bg: '#e0e7ff', text: '#4f46e5', border: '#c7d2fe' }; // Indigo
            if (sub.includes('seminar')) return { bg: '#fae8ff', text: '#c026d3', border: '#f5d0fe' }; // Fuchsia
            if (sub.includes('workshop')) return { bg: '#ffedd5', text: '#ea580c', border: '#fed7aa' }; // Orange
            return { bg: '#dbeafe', text: '#2563eb', border: '#bfdbfe' }; // Default Academic
        }

        if (cat === 'engagements') {
            if (sub.includes('visit')) return { bg: '#ede9fe', text: '#7c3aed', border: '#ddd6fe' }; // Purple
            if (sub.includes('meeting')) return { bg: '#d1fae5', text: '#059669', border: '#a7f3d0' }; // Green
            return { bg: '#d1fae5', text: '#059669', border: '#a7f3d0' }; // Default Engagement
        }

        // Other or fallback
        if (sub.includes('annual day')) return { bg: '#fce7f3', text: '#db2777', border: '#fbcfe8' }; // Pink
        if (sub.includes('festive')) return { bg: '#fef08a', text: '#ca8a04', border: '#fde047' }; // Yellow

        return { bg: '#f1f5f9', text: '#475569', border: '#e2e8f0' }; // Gray (Other)
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
            {/* Legend */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                {[
                    { label: 'Event', color: '#2563eb' },
                    { label: 'Engagements', color: '#059669' },
                    { label: 'Other', color: '#475569' }
                ].map(item => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color, boxShadow: '0 0 4px rgba(0,0,0,0.1)' }}></div>
                        {item.label}
                    </div>
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${daysCount}, minmax(0, 1fr))`, gap: '1rem', width: '100%' }}>
                {daysData.map(({ date, isToday, dayEvents, idx }) => {
                    const dateQueryStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

                    return (
                        <div
                            key={idx}
                            style={{
                                background: isToday ? '#f8fafc' : 'white',
                                border: isToday ? '1px solid #cbd5e1' : '1px solid #e2e8f0',
                                borderRadius: '12px',
                                padding: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '240px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                boxShadow: isToday ? '0 2px 4px rgba(0,0,0,0.02)' : 'none'
                            }}
                            onClick={() => navigate(`/calendar?date=${dateQueryStr}`)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'none';
                                e.currentTarget.style.boxShadow = isToday ? '0 2px 4px rgba(0,0,0,0.02)' : 'none';
                            }}
                        >
                            {/* Day Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {dayNames[date.getDay()]}
                                </span>
                                <span style={{
                                    fontSize: '1.25rem',
                                    fontWeight: isToday ? 800 : 600,
                                    color: isToday ? '#3b82f6' : '#1e293b',
                                    width: '28px', height: '28px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: isToday ? '#eff6ff' : 'transparent',
                                    borderRadius: '50%'
                                }}>
                                    {date.getDate()}
                                </span>
                            </div>

                            {/* Events List */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.4rem',
                                flex: 1,
                                overflowY: 'auto',
                                paddingRight: '2px'
                            }}>
                                {dayEvents.length > 0 ? (() => {
                                    let lastDisplayTime = null;
                                    return dayEvents.map((evt, eIdx) => {
                                        const eventCategory = evt.Category || 'Other';
                                        const eventSubType = evt.SubType || '';
                                        const colors = getEventColor(eventCategory, eventSubType);

                                        // Extract time
                                        const timeStr = evt.StartDateTime || evt.StartDate || evt.date;
                                        let displayTime = '';
                                        if (timeStr && timeStr.includes('T')) {
                                            const d = new Date(timeStr);
                                            displayTime = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                        }

                                        const showTimeSeparator = displayTime && displayTime !== lastDisplayTime;
                                        if (showTimeSeparator) {
                                            lastDisplayTime = displayTime;
                                        }

                                        const isNearest = globalNearestIdentifier === `${idx}-${eIdx}`;

                                        return (
                                            <div
                                                key={eIdx}
                                                ref={isNearest ? nearestEventRef : null}
                                                style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '2px' }}
                                            >
                                                {/* Time with horizontal line */}
                                                {showTimeSeparator && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: eIdx === 0 ? '0' : '6px', marginBottom: '2px' }}>
                                                        <span style={{ fontSize: '0.65rem', fontWeight: 900, color: '#000000', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
                                                            {displayTime}
                                                        </span>
                                                        <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, #e2e8f0 0%, transparent 100%)' }}></div>
                                                    </div>
                                                )}

                                                <div
                                                    style={{
                                                        flexShrink: 0,
                                                        background: colors.bg,
                                                        border: `1px solid ${colors.border}`,
                                                        borderRadius: '6px',
                                                        padding: '0.5rem',
                                                        color: colors.text,
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: '6px'
                                                    }}
                                                    title={evt.Title || evt.EventName || 'Untitled Event'}
                                                >
                                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: colors.text, marginTop: '4px' }}></div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
                                                        <div style={{
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500,
                                                            lineHeight: 1.3,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {evt.Title || evt.EventName || 'Untitled Event'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    });
                                })() : (
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: '0.75rem', fontStyle: 'italic', fontWeight: 500 }}>
                                        No events
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default WeeklyCalendar;
